import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, TextField, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  selectFiles,
  selectFormData,
  updateFormField,
} from "../../../ReduxStore/UserSlice";
import { SECURITY_OPTIONS } from "../../../utils/constants";
import DocumentUploadSection from "../../common/FileUpload/DocumentUploadSection";
import SelectField from "../../common/FormFields/SelectField";
import { base64ToFile } from "../../common/Util/Util";
import { requirementBriefValidation } from "../../common/ValidationRules/validationRules";

const RequirementBriefForm = React.forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const formData = useSelector(selectFormData);
  const files = useSelector(selectFiles);
  const {
    control,
    formState: { errors },
    trigger,
    setValue,
    watch,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      descriptionOfSecurity: "",
      securityOffered: "",
      coLetteral: null,
      orderCopy: null,
      contractCopy: null,
      certification: null,
      creditRating: null,
    },
  });

  const securityOffered = watch("securityOffered");

  React.useImperativeHandle(ref, () => ({
    validateForm: async () => {
      const result = await trigger();
      return result;
    },
  }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    dispatch(updateFormField({ field: name, value: value }));
  };
  const convertAndSetfile = (fieldName, fileData) => {
    if (Array.isArray(fileData)) {
      return fileData
        .map((file) => {
          if (file?.base64 && file?.filename) {
            return base64ToFile(file.base64, file.filename);
          } else if (file?.remote === true && (file?.url || file?.documentPath)) {
            return file;
          }
          return null;
        })
        .filter(Boolean);
    }
    if (fileData?.base64 && fileData?.filename) {
      return base64ToFile(fileData.base64, fileData.filename);
    } else if (
      fileData?.remote === true &&
      (fileData?.url || fileData?.documentPath)
    ) {
      return fileData;
    }
    return null;
  };

  useEffect(() => {
    setValue("descriptionOfSecurity", formData?.descriptionOfSecurity);
    setValue("securityOffered", formData?.securityOffered);
    setValue("orderCopy", convertAndSetfile("orderCopy", files?.orderCopy));
    setValue(
      "contractCopy",
      convertAndSetfile("contractCopy", files?.contractCopy)
    );
    setValue(
      "certification",
      convertAndSetfile("coLetteral", files?.certification)
    );
    setValue(
      "creditRating",
      convertAndSetfile("creditRating", files?.creditRating)
    );
    setValue("coLetteral", convertAndSetfile("coLetteral", files?.coLetteral));
  }, [files, formData, setValue]);
  const isSecurityNotAvailable =
    (securityOffered || "").toLowerCase() === "not_available";

  return (
    <Box>
      <Typography variant="body2" sx={{ color: "#666", mb: 3 }}>
        Step 5/5
      </Typography>
      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Provide details if any security is available, such as property address
          and estimated value<span style={{ color: "red" }}>*</span>
        </Typography>
        <Controller
          name="securityOffered"
          control={control}
          rules={requirementBriefValidation?.securityOffered}
          render={({ field }) => (
            <SelectField
              {...field}
              options={SECURITY_OPTIONS}
              required
              error={!!errors.securityOffered}
              helperText={errors?.securityOffered?.message}
              value={formData?.securityOffered}
              onChange={(e) => {
                field.onChange(e);
                handleChange(e);
              }}
            />
          )}
        />
      </Box>
      {console.log("securityOffered", securityOffered)}
      {!isSecurityNotAvailable && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Description of Security if available
            <span style={{ color: "red" }}>*</span>
          </Typography>
          <Controller
            name="descriptionOfSecurity"
            control={control}
            rules={requirementBriefValidation?.descriptionOfSecurity}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                required
                multiline
                rows={4}
                placeholder="e.g. Address of property and estimated value"
                error={!!errors?.descriptionOfSecurity}
                helperText={errors?.descriptionOfSecurity?.message}
                value={formData?.descriptionOfSecurity}
                onChange={(e) => {
                  field.onChange(e);
                  handleChange(e);
                }}
              />
            )}
          />
        </Box>
      )}

      <Box sx={{ mt: 4 }}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Security Documents:
        </Typography>
        <Typography
          variant="caption"
          sx={{ display: "flex", alignItems: "center", mb: 2 }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
          Upload files in JPEG, PNG, or PDF format, up to 5 MB in size.
        </Typography>

        <Box sx={{ mb: 2, position: "relative" }}>
          <DocumentUploadSection
            control={control}
            title="Collateral Document"
            name="coLetteral"
            setValue={setValue}
            errors={null}
            helptext={null}
            value={convertAndSetfile("coLetteral", files?.coLetteral)} // Just pass file data here; conversion is handled in effect
            multiple={true}
          />
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <DocumentUploadSection
            control={control}
            title="Order Copy"
            name="orderCopy"
            setValue={setValue}
            errors={null}
            helptext={null}
            value={convertAndSetfile("orderCopy", files?.orderCopy)} // Just pass file data here; conversion is handled in effect
            multiple={true}
          />

          <DocumentUploadSection
            control={control}
            title="Contract Copy"
            name="contractCopy"
            setValue={setValue}
            errors={null}
            helptext={null}
            value={convertAndSetfile("contractCopy", files?.contractCopy)} // Just pass file data here; conversion is handled in effect
            multiple={true}
          />
          <DocumentUploadSection
            control={control}
            title="Certification"
            name="certification"
            setValue={setValue}
            errors={null}
            helptext={null}
            value={convertAndSetfile("certification", files.certification)} // Just pass file data here; conversion is handled in effect
            multiple={true}
          />
          <DocumentUploadSection
            control={control}
            title="Credit Rating Document"
            name="creditRating"
            setValue={setValue}
            errors={null}
            helptext={null}
            value={convertAndSetfile("creditRating", files.creditRating)} // Just pass file data here; conversion is handled in effect
            multiple={true}
          />
          <DocumentUploadSection
            control={control}
            title="Other Document"
            name="other"
            setValue={setValue}
            errors={null}
            helptext={null}
            value={convertAndSetfile("other", files.other)} // Just pass file data here; conversion is handled in effect
            multiple={true}
            requireDocumentName={true}
          />
        </Box>
      </Box>
    </Box>
  );
});

export default RequirementBriefForm;
